import { STORAGE_KEYS, clearAuth } from '../config/storage'

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8005'

let refreshRequest = null

const getTokenUserId = (token) => {
  try {
    const encodedPayload = token.split('.')[1]
    const base64 = encodedPayload.replace(/-/g, '+').replace(/_/g, '/')
    const paddedBase64 = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=')
    return JSON.parse(window.atob(paddedBase64)).user_id
  } catch (error) {
    return null
  }
}

const refreshAccessToken = async (expectedUserId) => {
  const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN)
  if (!refreshToken) throw new Error('Session expired')

  if (!refreshRequest) {
    refreshRequest = fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken })
    })
      .then(async (response) => {
        if (!response.ok) throw new Error('Session expired')

        const data = await response.json()
        if (
          expectedUserId !== null &&
          String(data.user_id) !== String(expectedUserId)
        ) {
          throw new Error('Session user mismatch')
        }

        localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, data.access_token)
        localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, data.refresh_token)
        localStorage.setItem(STORAGE_KEYS.USER_ROLE, data.role)
        localStorage.setItem(STORAGE_KEYS.USERNAME, data.user_name)
        localStorage.setItem(STORAGE_KEYS.USER_ID, String(data.user_id))
        window.dispatchEvent(
          new CustomEvent('authSessionRefreshed', {
            detail: {
              accessToken: data.access_token,
              role: data.role,
              username: data.user_name
            }
          })
        )
        return data.access_token
      })
      .finally(() => {
        refreshRequest = null
      })
  }

  return refreshRequest
}

const sendAuthenticatedRequest = (url, options = {}, token) =>
  fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
      ...(token && { Authorization: `Bearer ${token}` })
    }
  })

const isExpiredTokenResponse = async (response) => {
  if (response.status !== 403) return false
  const error = await response
    .clone()
    .json()
    .catch(() => ({}))
  return error.detail === 'Signature has expired'
}

const authenticatedFetch = async (url, options) => {
  let token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN)
  let response = await sendAuthenticatedRequest(url, options, token)

  if (await isExpiredTokenResponse(response)) {
    try {
      token = await refreshAccessToken(getTokenUserId(token))
    } catch (error) {
      clearAuth()
      window.location.assign('/login')
      throw error
    }
    response = await sendAuthenticatedRequest(url, options, token)
  }

  if (response.status === 403) {
    const error = await response
      .clone()
      .json()
      .catch(() => ({}))
    if (error.detail === 'Admin access required') {
      clearAuth()
      window.location.assign('/login')
      throw new Error('Admin access required')
    }
  }

  return response
}

export const searchUsers = async ({
  username,
  active,
  createdFrom,
  createdTo,
  page = 1,
  size = 10
}) => {
  const params = new URLSearchParams({ page, size })
  if (username) params.set('username', username)
  if (active !== null && active !== undefined && active !== '')
    params.set('active', active)
  if (createdFrom) params.set('created_from', createdFrom)
  if (createdTo) params.set('created_to', createdTo)

  const response = await authenticatedFetch(
    `${API_BASE}/admin/users/search?${params}`
  )
  if (!response.ok) {
    const err = await response.json().catch(() => ({}))
    throw new Error(err.detail || `Failed to search users: ${response.status}`)
  }
  return await response.json()
}

export const approveUser = async (userId) => {
  const response = await authenticatedFetch(
    `${API_BASE}/admin/users/${userId}/approve`,
    {
      method: 'POST'
    }
  )
  if (!response.ok) {
    const err = await response.json().catch(() => ({}))
    throw new Error(err.detail || `Failed to approve user: ${response.status}`)
  }
  return await response.json()
}

export const getGoogleUsers = async () => {
  const response = await authenticatedFetch(`${API_BASE}/admin/users/google`)
  if (!response.ok) {
    const err = await response.json().catch(() => ({}))
    throw new Error(
      err.detail || `Failed to fetch Google users: ${response.status}`
    )
  }
  return await response.json()
}

export const rejectUser = async (userId) => {
  const response = await authenticatedFetch(
    `${API_BASE}/admin/users/${userId}/reject`,
    { method: 'DELETE' }
  )
  if (!response.ok) {
    const err = await response.json().catch(() => ({}))
    throw new Error(err.detail || `Failed to reject user: ${response.status}`)
  }
  return await response.json()
}

export const deactivateUser = async (userId) => {
  const response = await authenticatedFetch(
    `${API_BASE}/admin/users/${userId}/deactivate`,
    { method: 'DELETE' }
  )
  if (!response.ok) {
    const err = await response.json().catch(() => ({}))
    throw new Error(
      err.detail || `Failed to deactivate user: ${response.status}`
    )
  }
  return await response.json()
}

export const reactivateUser = async (userId) => {
  const response = await authenticatedFetch(
    `${API_BASE}/admin/users/${userId}/reactivate`,
    { method: 'POST' }
  )
  if (!response.ok) {
    const err = await response.json().catch(() => ({}))
    throw new Error(
      err.detail || `Failed to reactivate user: ${response.status}`
    )
  }
  return await response.json()
}

export const changeUserRole = async (userId, newRole) => {
  const params = new URLSearchParams({ new_role: newRole })
  const response = await authenticatedFetch(
    `${API_BASE}/admin/users/${userId}/role?${params}`,
    {
      method: 'PUT'
    }
  )
  if (!response.ok) {
    const err = await response.json().catch(() => ({}))
    throw new Error(
      err.detail || `Failed to change user role: ${response.status}`
    )
  }
  return await response.json()
}
